import type { ArticleListItem } from "@/types/api";
import { formatRelativeTime } from "@/lib/utils";

export const FRESH_HOURS = 48;
export const HOT_HOURS = 6;

export function articleTimestamp(article: Pick<ArticleListItem, "publish_at" | "created_at">): string {
  return article.publish_at || article.created_at;
}

/** Idade em horas (passado positivo). Futuro → negativo. */
export function hoursSince(value?: string | null, now = Date.now()): number | null {
  if (!value) return null;
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return null;
  return (now - ts) / 3_600_000;
}

export function isWithinHours(
  value: string | null | undefined,
  hours: number,
  now = Date.now(),
): boolean {
  const age = hoursSince(value, now);
  return age !== null && age >= 0 && age < hours;
}

export function isWithin48h(article: Pick<ArticleListItem, "publish_at" | "created_at">, now = Date.now()) {
  return isWithinHours(articleTimestamp(article), FRESH_HOURS, now);
}

export function isHotArticle(article: Pick<ArticleListItem, "publish_at" | "created_at">, now = Date.now()) {
  return isWithinHours(articleTimestamp(article), HOT_HOURS, now);
}

export type FreshnessKind = "now" | "hot" | "recent" | null;

export function getFreshnessKind(
  article: Pick<ArticleListItem, "publish_at" | "created_at">,
  now = Date.now(),
): FreshnessKind {
  const age = hoursSince(articleTimestamp(article), now);
  if (age === null || age < 0) return null;
  if (age < 1) return "now";
  if (age < HOT_HOURS) return "hot";
  if (age < FRESH_HOURS) return "recent";
  return null;
}

export function freshnessLabel(
  article: Pick<ArticleListItem, "publish_at" | "created_at">,
  now = Date.now(),
): string {
  const kind = getFreshnessKind(article, now);
  const value = articleTimestamp(article);
  if (kind === "now") return "Agora";
  if (kind === "hot") return "Ultimas horas";
  if (kind === "recent") {
    const relative = formatRelativeTime(value);
    return relative ? relative.replace(/^./, (c) => c.toUpperCase()) : "Recente";
  }
  return formatRelativeTime(value);
}

export function dedupeArticles(articles: ArticleListItem[]): ArticleListItem[] {
  const seen = new Set<number>();
  const out: ArticleListItem[] = [];
  for (const article of articles) {
    if (seen.has(article.id)) continue;
    seen.add(article.id);
    out.push(article);
  }
  return out;
}

function byPublishDesc(a: ArticleListItem, b: ArticleListItem): number {
  const ta = new Date(articleTimestamp(a)).getTime();
  const tb = new Date(articleTimestamp(b)).getTime();
  return tb - ta;
}

/**
 * Prioriza matérias das últimas 48h; completa com mais recentes se faltar
 * para manter o layout do módulo (até `limit` itens).
 */
export function selectFreshFeed(
  pools: ArticleListItem[][],
  limit = 4,
  now = Date.now(),
): ArticleListItem[] {
  const pool = dedupeArticles(pools.flat()).sort(byPublishDesc);
  const fresh = pool.filter((article) => isWithin48h(article, now));
  if (fresh.length >= limit) return fresh.slice(0, limit);
  const older = pool.filter((article) => !isWithin48h(article, now));
  return [...fresh, ...older].slice(0, limit);
}
